// Per-visitor "taste" profile. Anonymous, localStorage only, no PII.
// Now stores timestamped events (not just running totals) so we can apply
// the same recency multiplier as global popularity.
//
// Schema v2: backward-compatible read of v1 (running totals retained as
// a fallback bag, but new signals append to `events`).

import { POPULARITY } from "./heroAlgorithm.config";

const STORAGE_KEY = "cl_taste_v2";
const LEGACY_KEY = "cl_taste_v1";

export type SignalKind =
  | "project_view"
  | "project_dwell"
  | "filter_match"
  | "hero_click"
  | "hero_hover"
  | "repeat_project"
  | "repeat_sector";

export type TasteEvent = {
  kind: SignalKind;
  slug?: string;
  sector?: string;
  service?: string;
  points: number;
  /** ms since epoch */
  at: number;
};

export type TasteProfile = {
  events: TasteEvent[];
  visitedProjects: string[];   // for repeat detection
  visitedSectors: string[];
  shownRecently: string[];     // last N hero slugs (this session)
  signalCount: number;
  sessions: number;
  lastSeen: string;
  updatedAt: string;
};

const empty = (): TasteProfile => ({
  events: [],
  visitedProjects: [],
  visitedSectors: [],
  shownRecently: [],
  signalCount: 0,
  sessions: 0,
  lastSeen: new Date(0).toISOString(),
  updatedAt: new Date().toISOString(),
});

const safeRead = (): TasteProfile => {
  if (typeof window === "undefined") return empty();
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return { ...empty(), ...(JSON.parse(raw) as Partial<TasteProfile>) };
    // best-effort migration from v1
    const legacy = localStorage.getItem(LEGACY_KEY);
    if (legacy) {
      try {
        const v1 = JSON.parse(legacy) as { signalCount?: number };
        const t = empty();
        t.signalCount = v1.signalCount ?? 0;
        return t;
      } catch {
        /* ignore */
      }
    }
    return empty();
  } catch {
    return empty();
  }
};

const safeWrite = (p: TasteProfile) => {
  if (typeof window === "undefined") return;
  try {
    // keep events list bounded
    if (p.events.length > 500) p.events = p.events.slice(-500);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(p));
  } catch {
    /* quota — ignore */
  }
};

export const getTaste = (): TasteProfile => safeRead();

// ── Per-session caps so one tab can't dominate the local profile ──
const sessionCounts = new Map<string, number>();
const sessionCap = (kind: SignalKind, slug: string | undefined) => {
  const key = `${kind}:${slug ?? ""}`;
  const n = sessionCounts.get(key) ?? 0;
  if (n >= POPULARITY.caps.perKindPerSession) return false;
  sessionCounts.set(key, n + 1);
  return true;
};

export const recordSignal = (
  kind: SignalKind,
  payload: { slug?: string; sector?: string; service?: string; seconds?: number; pointsOverride?: number } = {},
) => {
  const { slug, sector, service, seconds, pointsOverride } = payload;
  if (!sessionCap(kind, slug)) return;

  let points = pointsOverride ?? POPULARITY.points[kind] ?? 0;

  // Dwell only counts if it crosses the meaningful threshold
  if (kind === "project_dwell") {
    if ((seconds ?? 0) < POPULARITY.dwellMinSeconds) return;
  }

  const t = safeRead();
  t.events.push({ kind, slug, sector, service, points, at: Date.now() });
  t.signalCount += 1;
  t.updatedAt = new Date().toISOString();

  if (kind === "project_view" && slug) {
    if (!t.visitedProjects.includes(slug)) t.visitedProjects.push(slug);
  }
  if (sector && !t.visitedSectors.includes(sector)) t.visitedSectors.push(sector);

  safeWrite(t);
};

/** Returns true if this visitor has previously visited the slug. */
export const isRepeatProject = (slug: string): boolean => {
  return safeRead().visitedProjects.includes(slug);
};
export const isRepeatSector = (sector: string): boolean => {
  return safeRead().visitedSectors.includes(sector);
};

/** Mark a slug as just-shown so the algorithm can avoid repeating it. */
export const noteShown = (slug: string, memory: number) => {
  const t = safeRead();
  t.shownRecently = [slug, ...t.shownRecently.filter((s) => s !== slug)].slice(0, memory);
  safeWrite(t);
};

/** Called once per app boot. Trims old events; bumps session count. */
export const beginSession = () => {
  const t = safeRead();
  const now = Date.now();
  const lastSeen = new Date(t.lastSeen).getTime();
  if (now - lastSeen >= 30 * 60 * 1000 || t.sessions === 0) {
    t.sessions += 1;
    t.shownRecently = [];
    sessionCounts.clear();
  }
  // Drop events older than 90 days
  const cutoff = now - 90 * 24 * 60 * 60 * 1000;
  t.events = t.events.filter((e) => e.at >= cutoff);
  t.lastSeen = new Date(now).toISOString();
  safeWrite(t);
};

export const resetTaste = () => {
  if (typeof window === "undefined") return;
  try {
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(LEGACY_KEY);
  } catch {
    /* ignore */
  }
  sessionCounts.clear();
};
